import { useMemo } from 'react';
import {
  useGetProjectQuery,
  useGetProjectBoardTabsQuery,
  useGetProjectBoardTabPermissionsQuery,
  useGetBoardUserQuery,
} from 'app/services/projectBoardApi';
import { getBootUserId } from 'hooks/useDashboardData';
import type { IActualTab } from 'models';

/**
 * Current user for the project board (fn['current.user']:
 * GET /api/v1.0/users/{id}, id from the server-injected config — the React
 * equivalent of body[data-user_id]).
 */
export function useBoardCurrentUser() {
  const id = getBootUserId();
  return useGetBoardUserQuery(id as number, { skip: id == null });
}

/**
 * fn['get.permissions'] (project fn/map.js lines 65–110): joins the CMS
 * per-role rows to the master tab catalog by tab_id → `actualTabs`
 * [{name, value, order, access, logo}] sorted by `order`. Row presence =
 * tab VISIBLE; `access` = tab EDITABLE (inventory §1.7 / parity 13, 16).
 *
 * Critical-task override (parity 17): when the project's critical task has
 * started AND the current user is a project-access user (access_type_id == 1),
 * every tab's `access` is forced false.
 *
 * Returns undefined until all four sources are loaded (the Backbone menu also
 * waits on project + permissions before rendering).
 */
export function useActualTabs(projectId: number): IActualTab[] | undefined {
  const { data: master } = useGetProjectBoardTabsQuery();
  const { data: cms } = useGetProjectBoardTabPermissionsQuery();
  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: user } = useBoardCurrentUser();

  return useMemo(() => {
    if (!master || !cms || !project || !user) return undefined;
    const lockAll =
      !!project.is_critical_task_started && user.access_type_id === 1;
    const tabs: IActualTab[] = [];
    cms.forEach((row) => {
      const found = master.find((item) => item.id === row.tab_id);
      if (!found) return; // master row missing → skip (Backbone would throw)
      tabs.push({
        name: found.name,
        value: found.value,
        order: found.order,
        access: lockAll ? false : row.access,
        logo: found.logo,
      });
    });
    return tabs.sort((a, b) => a.order - b.order);
  }, [master, cms, project, user]);
}

/**
 * Editability of one tab: `access` of its actualTabs entry (each tab view
 * computes `permissions.find(value==X).access`). false also when tabs are
 * still loading — the form locks itself until permissions arrive.
 */
export function useTabAccess(projectId: number, tabValue: string): boolean {
  const tabs = useActualTabs(projectId);
  return useMemo(
    () => !!tabs?.find((t) => t.value === tabValue)?.access,
    [tabs, tabValue]
  );
}
